import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RelayEnvironmentProvider } from 'react-relay';
import { RouterProvider } from 'react-router-dom';

import { LocaleProvider } from './i18n/LocaleProvider';
import { loadCatalog } from './i18n/load';
import { initialLocale, rememberedLocale } from './i18n/negotiate';
import { t } from './i18n/t';
import { relayEnvironment } from './relayEnv';
import { router } from './routes';
import { registerServiceWorker } from './sw/register';

/**
 * The root Suspense boundary stays even though every route has its own.
 *
 * It catches the gap before the first route renders. Without one, a component
 * that suspends with no boundary above it takes the whole tree down and
 * leaves `#root` empty — which is exactly how this page rendered nothing for
 * every commit between the `me` query landing and 0f3bdd3.
 */
// A component, not inline JSX: a `t()` in the fallback prop would run when this
// tree is built, before `LocaleProvider` has set the locale, and always say it
// in English.
function Starting() {
  return (
    <main className="mx-auto max-w-2xl p-6">
      <p className="text-base-content/60 text-sm" role="status">
        {t('common-starting')}
      </p>
    </main>
  );
}

// After load, so registering never competes with the first paint for the
// network. `serviceWorker` is absent outside a secure context.
registerServiceWorker('serviceWorker' in navigator ? navigator.serviceWorker : undefined, (run) => {
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
});

const initial = initialLocale(window.location.search, rememberedLocale(), navigator.languages ?? []);

// The first paint waits for a non-English catalog rather than flashing
// English and remounting a moment later. A catalog that fails to load opens
// the page in English.
void (initial === 'qps' ? Promise.resolve(true) : loadCatalog(initial)).then((loaded) => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <LocaleProvider initial={loaded ? initial : 'en'}>
        <RelayEnvironmentProvider environment={relayEnvironment}>
          <Suspense fallback={<Starting />}>
            <RouterProvider router={router} />
          </Suspense>
        </RelayEnvironmentProvider>
      </LocaleProvider>
    </StrictMode>,
  );
});
